import { Platform } from '../models/api';
import { AiService, FindingAiService, AgentClientApplication } from '../models/api/agentStep.model';
import { CombinedIntegration } from '../models/api/integration.model';

/**
 * Maps Platform to corresponding AI Services
 * This mapping determines which AI services should be available based on active platforms
 */
export const platformToAiServicesMap: Partial<Record<Platform, AiService[]>> = {
  [Platform.powerplatform]: [
    AiService.m365Copilot,
    AiService.copilotStudio,
    AiService.powerAutomate,
    AiService.powerApps,
    AiService.powerPages,
  ],
  [Platform.outpostPowerPlatform]: [
    AiService.m365Copilot,
    AiService.copilotStudio,
    AiService.powerAutomate,
    AiService.powerApps,
    AiService.powerPages,
  ],
  [Platform.salesforce]: [AiService.salesforceEinstein, AiService.agentforce],
  [Platform.chatGptEnterprise]: [AiService.chatGptEnterprise, AiService.openaiCodex],
  [Platform.amazonBedrock]: [AiService.bedrock, AiService.bedrockAgentCore],
  [Platform.google]: [AiService.vertexAi],
  [Platform.serviceNow]: [AiService.serviceNow],
  [Platform.devices]: [
    AiService.claude,
    AiService.perplexity,
    AiService.cursor,
    AiService.windsurf,
    AiService.tabnine,
    AiService.amazonQ,
    AiService.dia,
    AiService.agentKit,
  ],
  [Platform.cursor]: [AiService.cursor],
  [Platform.customAgent]: [AiService.customAgent],
  // Default for other platforms
  [Platform.workato]: [],
  [Platform.powerBI]: [],
  [Platform.budibase]: [],
  [Platform.airtable]: [],
};

/**
 * Maps Platform to corresponding Finding AI Services (subset for AIDR features)
 */
export const platformToFindingAiServicesMap: Partial<Record<Platform, FindingAiService[]>> = {
  [Platform.powerplatform]: [
    FindingAiService.m365Copilot,
    FindingAiService.copilotStudio,
    FindingAiService.azureAiFoundry,
  ],
  [Platform.outpostPowerPlatform]: [
    FindingAiService.m365Copilot,
    FindingAiService.copilotStudio,
    FindingAiService.azureAiFoundry,
  ],
  [Platform.salesforce]: [FindingAiService.salesforceEinstein, FindingAiService.agentforce],
  [Platform.chatGptEnterprise]: [FindingAiService.chatGptEnterprise, FindingAiService.openaiCodex],
  [Platform.amazonBedrock]: [FindingAiService.bedrock, FindingAiService.bedrockAgentCore],
  [Platform.google]: [FindingAiService.vertexAi],
  [Platform.serviceNow]: [FindingAiService.serviceNow],
  [Platform.devices]: [],
  [Platform.cursor]: [],
  [Platform.customAgent]: [FindingAiService.customAgent],
  [Platform.workato]: [],
  [Platform.powerBI]: [],
  [Platform.budibase]: [],
  [Platform.airtable]: [],
};

/**
 * Maps AI Services to corresponding Client Applications
 * Determines which client applications should be shown based on active AI services
 */
export const aiServiceToClientApplicationsMap: Record<string, AgentClientApplication[]> = {
  [AiService.m365Copilot]: [
    AgentClientApplication.office,
    AgentClientApplication.teams,
    AgentClientApplication.word,
    AgentClientApplication.excel,
    AgentClientApplication.powerpoint,
    AgentClientApplication.outlook,
    AgentClientApplication.onenote,
    AgentClientApplication.appchat,
    AgentClientApplication.sharepoint,
    AgentClientApplication.stream,
    AgentClientApplication.forms,
    AgentClientApplication.bing,
  ],
  [AiService.copilotStudio]: [AgentClientApplication.agentWebsite, AgentClientApplication.testing],
  [AiService.chatGptEnterprise]: [AgentClientApplication.chatGpt],
  [AiService.azureAiFoundry]: [AgentClientApplication.azureAiFoundry],
  [AiService.vertexAi]: [AgentClientApplication.vertexAi],
  [AiService.serviceNow]: [AgentClientApplication.serviceNow],
  [AiService.agentforce]: [AgentClientApplication.autonomous],
  [AiService.openaiCodex]: [
    AgentClientApplication.CODEX_CLI,
    AgentClientApplication.CODEX_WEB,
    AgentClientApplication.CODEX_IDE_VSCODE,
    AgentClientApplication.CODEX_GITHUB_ACTION,
    AgentClientApplication.CODEX_SDK_TS,
    AgentClientApplication.CODEX_SERVICE_EXEC,
    AgentClientApplication.CODEX_DESKTOP_APP,
    AgentClientApplication.CODEX_UNKNOWN_DEFAULT,
  ],
};

/**
 * Gets active AI services from integrations for Activity/Agent Steps pages
 */
export function getActiveAiServicesFromIntegrations(
  integrations: (CombinedIntegration | { platform: Platform })[]
): Set<AiService> {
  const activeServices = new Set<AiService>();

  for (const integration of integrations) {
    const services = platformToAiServicesMap[integration.platform as Platform];
    if (services) {
      services.forEach((service: AiService) => activeServices.add(service));
    }
  }

  return activeServices;
}

/**
 * Gets active Finding AI services from integrations for Findings page
 */
export function getActiveFindingAiServicesFromIntegrations(
  integrations: (CombinedIntegration | { platform: Platform })[]
): Set<FindingAiService> {
  const activeServices = new Set<FindingAiService>();

  for (const integration of integrations) {
    const services = platformToFindingAiServicesMap[integration.platform as Platform];
    if (services) {
      services.forEach((service: FindingAiService) => activeServices.add(service));
    }
  }

  return activeServices;
}

/**
 * Gets active client applications from active AI services
 */
export function getActiveClientApplicationsFromAiServices(
  activeAiServices: Set<AiService>
): Set<AgentClientApplication> {
  const activeClientApps = new Set<AgentClientApplication>();

  for (const service of activeAiServices) {
    const clientApps = aiServiceToClientApplicationsMap[service];
    if (clientApps) {
      clientApps.forEach((app) => activeClientApps.add(app));
    }
  }

  return activeClientApps;
}

/**
 * Checks if M365/PowerPlatform is active (for conditional Chat Mode filter)
 */
export function isPowerPlatformActive(integrations: (CombinedIntegration | { platform: Platform })[]): boolean {
  return integrations.some(
    (integration) =>
      integration.platform === Platform.powerplatform || integration.platform === Platform.outpostPowerPlatform
  );
}

/**
 * Checks if Azure AI Foundry is active (for conditional Environment Name filter)
 */
export function isAzureAiFoundryActive(integrations: (CombinedIntegration | { platform: Platform })[]): boolean {
  // Azure AI Foundry may be available through multiple platforms
  // Check if any integration has a platform that includes Azure AI Foundry services
  const activeServices = getActiveAiServicesFromIntegrations(integrations);
  return activeServices.has(AiService.azureAiFoundry);
}
